import {
  ESdChangeReason,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IKType,
  IKTypeField,
  ISymbol
} from "@kuika/kuika-cl-model";
import { IWFParameter, IWorkFlow } from "@kuika/kuika-cl-model/src/models/workflow";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { EActionPanelStatus, IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import { ActionSymbolWithChild } from "./action-symbol-with-child";

export interface IWFGenericTasksSymbolsGroupState {
  fromSingleProcess: boolean;
  action: IAction;
  actionDefinition?: IActionDefinitionDto;
  selectedProcess: IWorkFlow | undefined;
  processParameters: IWFParameter[];
  selectedProcessParameters: IWFParameter[];
}

export interface IWFGenericTasksSymbolsGroupProps {
  action: IAction;
  selectedProcess: IWorkFlow | undefined;
  selectedProcessParameters: IWFParameter[] | [];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  onActionParameterSelected?: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
}

class WFGenericTasksSymbolsGroup extends PureSdComponent<
  IWFGenericTasksSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IWFGenericTasksSymbolsGroupState
> {
  constructor(props: IWFGenericTasksSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      fromSingleProcess: !!this.props.selectedProcess,
      action: this.props.action,
      actionDefinition: undefined,
      selectedProcess: undefined,
      processParameters: [],
      selectedProcessParameters: []
    };
  }

  componentDidMount(): void {
    const actionDefinition = this.props.sd.actionDefinitions.find(
      (ad) => ad.id === this.state.action.actionDefinitionId
    );
    this.setState({
      selectedProcess: this.props.selectedProcess,
      selectedProcessParameters: this.props.selectedProcessParameters,
      actionDefinition
    });
  }

  toggleFromSingleProcess = (value) => {
    if (value === false) {
      const { action } = this.state;
      const process = action?.parameters.find((p) => p.name === "ProcessDefinitionId");
      if (process) process.symbol.fixedValue = "";
      const processParameters = action?.parameters.find((p) => p.name === "ProcessParameters");
      if (processParameters) processParameters.symbol.fixedValue = "";
      const valuesToUpdate = {
        actionParameters: this.state.action.parameters,
        actionReturnTypes: {
          elements: []
        } as unknown as IKType
      } as IActionPanelStatesCanBeUpdatedFromChild;
      this.props.updateValues(valuesToUpdate, ESdChangeReason.ActionDescriptionChanged);
      this.setState({
        selectedProcess: undefined,
        selectedProcessParameters: []
      });
    }
    this.setState({ fromSingleProcess: value });
  };

  handleProcessChange = (value: string | null): void => {
    const { action } = this.state;
    const process = action?.parameters.find((p) => p.name === "ProcessDefinitionId");
    // The kit reports a cleared single select as `null`; antd reported `undefined`, and a
    // `fixedValue` of `null` would be serialised into the screen where nothing was written
    // before. `undefined` keeps the key out of the JSON, as it was.
    if (process) process.symbol.fixedValue = value ?? undefined;
    const valuesToUpdate = {
      actionParameters: this.state.action.parameters
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, ESdChangeReason.ActionDescriptionChanged);
    this.setState({
      selectedProcess: this.props.sd.workFlowList.find((wf) => wf.id === value)
    });
  };

  handlePropertySelection = (value: string[]): void => {
    const { action } = this.state;
    const processParameters = action?.parameters.find((p) => p.name === "ProcessParameters");
    if (processParameters) processParameters.symbol.fixedValue = value.join(",");

    const selectedProcessParameters = (this.state.selectedProcess?.parameters || []).filter((p) =>
      value.includes(p.id)
    );

    const actionDynamicReturnTypes = {
      elements: []
    } as unknown as IKType;

    if (actionDynamicReturnTypes?.elements) {
      selectedProcessParameters.forEach((field) => {
        actionDynamicReturnTypes.elements?.push({
          elementTypeModel: undefined,
          name: field.name,
          prefix: "data",
          type: field.dataType.toString() as unknown as IKType
        } as IKTypeField);
      });
    }

    const valuesToUpdate = {
      actionParameters: this.state.action.parameters,
      actionReturnTypes: actionDynamicReturnTypes
    } as IActionPanelStatesCanBeUpdatedFromChild;

    this.props.updateValues(valuesToUpdate, ESdChangeReason.ActionDescriptionChanged);

    this.setState({
      selectedProcessParameters
    });
  };

  render() {
    const processOptions: KuikaSelectboxOption[] = (this.props.sd.workFlowList ?? []).map(
      (wf: IWorkFlow): KuikaSelectboxOption => ({ key: wf.id, value: wf.id, label: wf.name })
    );
    const processParameterOptions: KuikaSelectboxOption[] = (this.state.selectedProcess?.parameters ?? []).map(
      (p: IWFParameter): KuikaSelectboxOption => ({ key: p.id, value: p.id, label: p.name })
    );

    return (
      <>
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <KuikaSwitch
            checked={this.state.fromSingleProcess}
            disabled={false}
            label="Select from single process"
            onChange={(checked) => {
              this.toggleFromSingleProcess(checked);
            }}
          />
        </div>
        <div style={{ marginBottom: 12, display: this.state.fromSingleProcess ? "block" : "none" }}>
          <div className="kuika_symbol-picker__managed-db">
            <div style={{ width: "100%" }}>
              <div>
                <label className="kuika_properties_label_text">Process</label>
                <div>
                  <KuikaSelectbox
                    style={{ width: "100%" }}
                    searchable
                    className="kuika__action-parameters__item-select"
                    placeholder="Select a process"
                    allowClear={true}
                    onChange={this.handleProcessChange}
                    options={processOptions}
                    value={this.state.selectedProcess?.id ?? null}
                  />
                </div>
              </div>
            </div>
            <div style={{ width: "100%" }}>
              <div>
                <label className="kuika_properties_label_text">Process Parameters</label>
                <div>
                  <KuikaSelectbox
                    style={{ width: "100%" }}
                    // A multiple select draws its search input unless it is told otherwise, so
                    // asking for search keeps the one antd was drawing here.
                    searchable
                    className="kuika__action-parameters__item-select"
                    placeholder="Select parameters to get"
                    mode="multiple"
                    aria-multiline={true}
                    allowClear={true}
                    onChange={(value: string[]): void => {
                      this.handlePropertySelection(value);
                    }}
                    options={processParameterOptions}
                    value={(this.state.selectedProcessParameters || []).map((p) => p.id)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {this.props.action.parameters
          ?.filter((p) => !["ProcessDefinitionId", "ProcessParameters"].includes(p.name))
          .filter((p) =>
            p.isOptionalParameter === true ? (p.symbol.fixedValue ? true : p.isExternalVisible === true) : true
          )
          ?.map((p, index) => {
            return (
              <div
                key={`symbolPicker-${this.props.action.id}-${p.name}-${index}`}
                className={p.isDeletedOnSource === true ? "isDeletedOnSource" : ""}
              >
                <ActionSymbolWithChild
                  parameter={p}
                  action={this.props.action}
                  actionDefinition={this.state.actionDefinition!}
                  actionPanelStatus={EActionPanelStatus.new}
                  onActionParameterSelected={(p, symbol, changeReason) =>
                    this.handleSelectSymbolPickerValue(p, symbol, changeReason)
                  }
                ></ActionSymbolWithChild>
              </div>
            );
          })}
      </>
    );
  }

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const params = _.cloneDeep(this.props.action.parameters);
    const param = params.find((param) => param.name === parameter.name);
    if (param) param.symbol = symbol;
    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const wfGenericTasksSymbolsGroup = withContext<IWFGenericTasksSymbolsGroupProps>(
  withSD<IWFGenericTasksSymbolsGroupProps>(WFGenericTasksSymbolsGroup)
);
export { wfGenericTasksSymbolsGroup as WFGenericTasksSymbolsGroup };
